import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";
import AnimatedCard from "../../../components/AnimatedCard/AnimatedCard";
import { resetPassword } from "../../../services/authApiService";
import { Button, Input, Alert } from "@repo/ui";
import * as Sentry from "@sentry/react";

const LOCALE = "modules_Registration_pages_PasswordReset";

const PasswordReset: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const token = searchParams.get("token");

  useEffect(() => {
    setLoading(false);
  }, []);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return t("error.short-password");
    if (!/[A-Z]/.test(password)) return t("error.missing-uppercase");
    if (!/[a-z]/.test(password)) return t("error.missing-lowercase");
    if (!/\d/.test(password)) return t("error.missing-number");
    return null;
  };

  const handlePasswordReset = async () => {
    setStatusMessage(null);
    setError(null);

    if (!token) {
      setError(t("error.missing-token"));
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("error.password-mismatch"));
      return;
    }

    try {
      await resetPassword(token, newPassword);
      setStatusMessage(t("success-message"));
      setTimeout(() => navigate("/"), 3000);
    } catch (error) {
      Sentry.captureException(error);
      setError(t("error.generic"));
    }
  };

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  return (
    <div className="flex justify-center flex-col items-center min-h-[calc(100vh-6rem)] bg-background py-8">
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        <div className="space-y-8">
          <p className="text-sm text-gray-700">{t("instruction")}</p>

          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="input-border w-full"
            placeholder={t("new-password-placeholder")}
          />
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input-border w-full"
            placeholder={t("confirm-password-placeholder")}
          />
          <Button
            type="primary"
            onClick={handlePasswordReset}
            className="w-full"
          >
            {t("submit-button")}
          </Button>
          {statusMessage && <Alert message={statusMessage} level="success" />}
          {error && <Alert message={error} level="error" />}
        </div>
      </AnimatedCard>
    </div>
  );
};

export default withTranslations(LOCALE)(PasswordReset);
